import { Component, Inject, Input, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AlarmGroupConsoleSettingsService } from '@service/alarm-group-console-settings.service';
import {
    AlarmGroupConsolePayload,
    ConsoleSettingEvent,
    OutputSettingType,
    OutputAddress,
    SettingsAddress,
    AlarmGroup,
    OutputSettingMode,
    SignalWidthMin,
    SignalWidthMax,
} from 'icreate/common/models/alarm-group';
import { SensorTagSlot } from 'icreate/common/models/sensor-cable-tag';
import { getSlotPinNum } from 'icreate/common/models/slot';
import { Vessel } from 'icreate/common/models/vessel';
import * as _ from 'lodash';

@Component({
    selector: 'app-alarm-group-ouput-settings',
    templateUrl: './alarm-group-ouput-settings.component.html',
    styleUrl: './alarm-group-ouput-settings.component.css',
    standalone: false,
})
export class AlarmGroupOuputSettingsComponent {
    isLoading: boolean = false;
    consoleSettingEvent: ConsoleSettingEvent = ConsoleSettingEvent.UNDEFINED;

    outputSettings: OutputAddress[] = [];
    initialSetting: OutputAddress[] = [];
    vesselSlots: SensorTagSlot[];

    groupName: string = '';

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            alarmGroup: AlarmGroup;
            vessel: Vessel;
            vesselSlots: SensorTagSlot[];
        },
        private dialogRef: MatDialogRef<AlarmGroupOuputSettingsComponent>,
        private alarmGroupConsoleSettingsService: AlarmGroupConsoleSettingsService
    ) {}

    async ngOnInit() {
        this.isLoading = true;
        const settings = this.data.alarmGroup.outputSettings;
        if (settings) settings.sort((a, b) => a.index - b.index);
        this.outputSettings = settings;
        this.initialSetting = _.cloneDeep(this.outputSettings);
        this.groupName = this.data.alarmGroup.name;

        if (!this.outputSettings || this.outputSettings.length == 0) {
            this.outputSettings = [];
            const outputSetting = new OutputAddress(1);
            this.outputSettings.push(outputSetting);
            this.consoleSettingEvent = ConsoleSettingEvent.CREATING;
        } else {
            this.consoleSettingEvent = ConsoleSettingEvent.UPDATING;
        }

        this.vesselSlots = this.data.vesselSlots;
        this.isLoading = false;
    }

    clearForm() {
        this.dialogRef.close(this.initialSetting);
    }

    async save() {
        if (this.isButtonDisabled()) return;
        try {
            this.isLoading = true;
            const payload: AlarmGroupConsolePayload = {
                vesselId: this.data.vessel.id,
                alarmGroupId: this.data.alarmGroup.id,
                event: this.consoleSettingEvent,
                ouputs: [],
            };
            for (let i = 0; i < this.outputSettings.length; i++) {
                const setting = this.outputSettings[i];
                const settingAddress = setting.address;
                const outputSlot = this.vesselSlots.find(
                    slot =>
                        settingAddress.fcuNum === slot.fcuNum &&
                        (!settingAddress.fuNum || settingAddress.fuNum === slot.fuNum) &&
                        settingAddress.slotNum === slot.no
                );
                const isOneShot = setting.type == OutputSettingType.ONE_SHOT;
                payload.ouputs.push({
                    alarmGroupId: this.data.alarmGroup.id,
                    slotId: outputSlot.slotId,
                    terminalNum: settingAddress.terminal,
                    index: setting.index,
                    type: setting.type,
                    mode: setting.mode,
                    id: setting.address.settingId,
                    signal: isOneShot ? setting.signal : null,
                });
            }
            await this.alarmGroupConsoleSettingsService.save(payload);
            this.initialSetting = _.cloneDeep(this.outputSettings);
            this.dialogRef.close(this.outputSettings);
        } catch (err) {
            console.log(err);
        } finally {
            this.isLoading = false;
        }
    }

    isButtonDisabled() {
        if (_.isEqual(this.outputSettings, this.initialSetting)) return true;
        let hasInvalidAddress = false;
        for (let i = 0; i < this.outputSettings.length; i++) {
            if (hasInvalidAddress) continue;
            const setting = this.outputSettings[i];
            if (!setting.type || setting.type == OutputSettingType.UNDEFINED) hasInvalidAddress = true;
            if (!setting.mode || setting.mode == OutputSettingMode.UNDEFINED) hasInvalidAddress = true;
            if (setting.type == OutputSettingType.ONE_SHOT) {
                if (isNaN(setting.signal) || setting.signal < SignalWidthMin || setting.signal > SignalWidthMax)
                    hasInvalidAddress = true;
            }
            if (!this.isValidSettingAddress(setting.address)) hasInvalidAddress = true;
        }
        return hasInvalidAddress;
    }

    isValidSettingAddress(settingsAddress: SettingsAddress) {
        if (!settingsAddress) return false;
        if (isNaN(settingsAddress.fcuNum) || isNaN(settingsAddress.slotNum) || !settingsAddress.terminal) return false;
        const slot = this.vesselSlots.find(
            slot =>
                settingsAddress.fcuNum === slot.fcuNum &&
                (!settingsAddress.fuNum || settingsAddress.fuNum === slot.fuNum) &&
                settingsAddress.slotNum === slot.no
        );

        if (!slot) return false;
        const maxPin = getSlotPinNum(slot.ioType, slot.terminal);
        if (maxPin < settingsAddress.terminal) return false;

        return true;
    }

    addSetting() {
        if (this.isAddSettingDisabled()) return;
        const newIndex = this.outputSettings.length + 1;
        const outputSettings = new OutputAddress(newIndex);
        this.outputSettings.push(outputSettings);
    }

    isAddSettingDisabled() {
        if (!this.outputSettings) return true;
        return this.outputSettings.length >= 4;
    }

    handleDeleteSetting(outputSetting: OutputAddress) {
        if (outputSetting.index == 1) return;
        const settingIndex = outputSetting.index - 1;
        this.outputSettings.splice(settingIndex, 1);
        this.outputSettings = this.outputSettings.map((setting, i) => {
            if (setting.index < outputSetting.index) return setting;
            setting.index = setting.index - 1;
            return setting;
        });
    }
}
